import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styled from '~/app/components/styled'
import Item, {
  IOrderItem,
  ItemState,
  ItemType,
} from '~/app/components/order-cancel/orderItems/item'
import { isoToFormat } from '~/app/utils/useIsoToFormat'
import { Icon_info_fill } from '@yanolja-rnd/ya-com-system-icon'
import Checkbox from '~/app/components/common/Checkbox/YaComCheckbox'
import { Typography } from '@yanolja-rnd/ya-com-core'

const Container = styled.div<{ cancelable: boolean }>`
  width: 100%;
  background-color: ${({ theme }) => theme.color.white};
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  flex-shrink: 0;
  padding-bottom: ${({ cancelable }) => (cancelable ? `0` : '5rem')};
`

const Header = styled.div`
  padding: 1.2rem 2rem;

  & .date {
    margin-top: 0.3rem;
    ${Typography('body2').regular};
    color: ${({ theme }) => theme.color.gray600};
  }
`

const ItemContainer = styled.div`
  padding: 0 2rem;
  & > * {
    border-top: 1px solid ${({ theme }) => theme.color.gray200};
    &:first-of-type {
      border-top: 0;
    }
  }
`

const AllCheckText = styled.span`
  margin-left: 1.2rem;
  ${Typography('body1').regular};
  color: ${({ theme }) => theme.color.gray900};
`

const Caution = styled.div`
  position: relative;
  ${Typography('body2').regular};
  color: ${({ theme }) => theme.color.gray900};

  .contents {
    display: grid;
    grid-template-columns: 1.6rem 1fr;
    grid-column-gap: 0.8rem;
    grid-row-gap: 0.2rem;
    padding: 1.4rem 1.6rem;
    border-radius: 0.4rem;
    border: 0.1rem solid ${({ theme }) => theme.color.yaorange200};
    background-color: ${({ theme }) => theme.color.yaorange50};

    .title {
      font-weight: bold;
      color: ${({ theme }) => theme.color.yaorange};
    }

    .description {
      display: inline-block;
      ${Typography('body2.multiline').regular};
    }
  }

  .date {
    margin-top: 0.8rem;
    text-align: right;
    color: ${({ theme }) => theme.color.gray600};
  }
`
const InfoIcon = styled(Icon_info_fill)`
  grid-row-start: 1;
  grid-row-end: 3;
  margin-top: 0.1rem;
  path {
    fill: ${({ theme }) => theme.color.yaorange};
  }
`

const SelectAllCheckboxWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2rem;
`
const SelectAllCheckbox = styled(Checkbox)`
  img {
    width: 2rem;
    height: 2rem;
  }
`

interface IOrderStatus {
  cancelable: boolean
  partialCancelable: boolean
  message: {
    headLine: string
    description: string
  }
}

interface Props {
  orderItems: IOrderItem[]
  initialCancelItems: OrderItem[]
  date: string
  orderStatus: IOrderStatus
  onChangeCancelItems: (cancelItems: IOrderItem[]) => void
}

const Index: FC<Props> = ({
  orderItems,
  initialCancelItems,
  date,
  orderStatus,
  onChangeCancelItems,
}) => {
  const cancelableItems = useMemo(
    () => orderItems.filter(v => v.status === ItemState.CANCELABLE),
    [orderItems]
  )
  const [cancelItems, setCancelItems] = useState<IOrderItem[]>(
    orderStatus.cancelable && !orderStatus.partialCancelable ? orderItems : initialCancelItems
  )

  const items = useMemo(() => {
    const onCheckedChange = (id, checked) => {
      const newCancelItems = checked
        ? cancelItems.concat(orderItems.filter(v => v.orderItemId === id))
        : cancelItems.filter(v => v.orderItemId !== id)
      setCancelItems(newCancelItems)
    }
    return orderItems.map((item, index) => {
      const isCancelItems = !!cancelItems.find(v => v.orderItemId === item.orderItemId)

      return (
        <Item
          key={index}
          id={item.id}
          orderItemId={item.orderItemId}
          status={item.status}
          title={item.title}
          subTitle={item.subTitle}
          optionTitle={item.optionTitle}
          ratePlanTitle={item.ratePlanTitle}
          info={item.info}
          options={item.options}
          commissionAmount={item.commissionAmount}
          price={item.price}
          productUrl={item.productUrl}
          imageSrc={item.imageSrc}
          itemType={item.itemType}
          cancelPolicy={item.cancelPolicy}
          cancelPolicies={item.cancelPolicies}
          order={item.order}
          isPendingReservation={item.isPendingReservation}
          badges={item.badges}
          checked={isCancelItems}
          onCheckedChange={onCheckedChange}
          showCheckbox={orderStatus.cancelable && orderStatus.partialCancelable}
          transportationCompanyCode={item.transportationCompanyCode}
          requestedAt={date}
        />
      )
    })
  }, [orderItems, cancelItems])

  useEffect(() => {
    onChangeCancelItems(cancelItems)
  }, [cancelItems])

  const onClickSelectAll = useCallback((checked: boolean) => {
    setCancelItems(checked ? orderItems.filter(v => v.status === ItemState.CANCELABLE) : [])
  }, [])

  const isAvailableCancelAll = useMemo(
    () => !!orderItems.length && orderStatus.cancelable && orderStatus.partialCancelable,
    [orderStatus, orderItems]
  )

  const isShowCancelWarning = useMemo(
    () => !!orderItems.length && (orderStatus.message.headLine || orderStatus.message.description),
    [orderStatus, orderItems]
  )

  const requestedAt = useMemo(() => {
    return isoToFormat(date, 'yyyy.MM.dd (ccc) HH:mm')
  }, [date])

  const isSelectAll = useMemo(
    () => cancelItems.length === cancelableItems.length,
    [cancelItems, cancelableItems]
  )

  return (
    <Container cancelable={orderStatus.cancelable}>
      <Header>
        {isShowCancelWarning && (
          <Caution>
            <div className={'contents'}>
              <InfoIcon size={1.6} unit="rem" />
              {orderStatus.message.headLine && (
                <h3 className={'title'}>{orderStatus.message.headLine}</h3>
              )}
              <span className={'description'}>{orderStatus.message.description}</span>
            </div>
            {!orderStatus.cancelable && <div className={'date'}>{requestedAt} 기준</div>}
          </Caution>
        )}
        {isAvailableCancelAll && (
          <SelectAllCheckboxWrapper>
            <SelectAllCheckbox checked={isSelectAll} onClick={() => onClickSelectAll(!isSelectAll)}>
              <AllCheckText data-testid={'orderCancel-select-all-items'}>전체 선택</AllCheckText>
            </SelectAllCheckbox>
            <span className={'date'}>{requestedAt} 기준</span>
          </SelectAllCheckboxWrapper>
        )}
      </Header>
      <ItemContainer>{items}</ItemContainer>
    </Container>
  )
}

export default Index
export type OrderItem = IOrderItem
export { ItemState, ItemType }
